/**
 * Finishing onboarding: one call, resumable across a page load.
 *
 * It used to be a page transition. `Onboarding` navigated to
 * `/quiz?saveAnswers&nav=…`, `Quiz` mounted, noticed the query, and ran the
 * mutation on its behalf. That is why `/quiz` could not simply be the answer
 * editor — it was also the save handler for two other flows and the legacy
 * signup surface — and why `?saveAnswers` had to be threaded through the
 * forwarding rules for signed-out visitors.
 *
 * The round-trip was standing in for something real, though: the answers have
 * to survive the flow being interrupted. Today sign-in is a popup and the page
 * lives, but redirect-based SSO destroys it — the tab navigates to the
 * provider and comes back on a fresh load with every ref and every piece of
 * component state gone.
 *
 * So completion is persisted, not held. `markPending` writes the intent before
 * anything can interrupt; the draft answers are already in `localStorage`. On
 * return, `useOnboardingSave` finds the marker and finishes the job. Nothing
 * depends on a particular URL surviving, on which page the provider returns
 * to, or on the component that started it still being mounted.
 */

import { useCallback, useEffect, useRef } from "react";
import { useUpdateProfile, useInvalidateProfile, useProfile } from "./api";
import { useAppNavigation } from "./platform";
import { useAuth } from "./platform/AuthProvider";
import tracking from "./tracking";
import { getStoredFacebookAttribution } from "./tracking/attribution";
import { getStoredAdEventType } from "./quizEventType";
import { resolvePostQuizDestination } from "./postSignupNav";
import { useGuidedPostSignupBooking } from "./features";
import { isQuizComplete } from "@shared/quizCompletion";

/** Where the flow's draft answers live. Written on every answer. */
export const DRAFT_KEY = "quizAnswers";
/** Set the moment the user finishes, cleared only once the save lands. */
const PENDING_KEY = "onboardingPendingSave";

interface PendingSave {
  /** The `nav` destination the flow was started with, if any. */
  nav: string | null;
  /**
   * A seat was booked inside the flow. Survives the round trip because the
   * destination depends on it: the guided post-signup booking screen is an
   * upgrade for people who have *not* booked, and a detour for anyone who has.
   */
  booked?: boolean;
  /**
   * `nav` is the exact path to leave for, not a hint for
   * `resolvePostQuizDestination` to rewrite. Set by the escape hatches: a
   * user who has just declined to pick a night is not to be upgraded into
   * the guided booking screen on the way out.
   */
  exact?: boolean;
}

function readPending(): PendingSave | null {
  try {
    const raw = localStorage.getItem(PENDING_KEY);
    return raw ? (JSON.parse(raw) as PendingSave) : null;
  } catch {
    return null;
  }
}

/**
 * Records that the user has finished and their answers are owed to the server.
 *
 * Called *before* anything that might not come back — the save itself, or a
 * sign-in that navigates away. If the page dies in between, the marker and the
 * draft are both on disk and the next load completes it.
 */
export function markOnboardingPending(
  nav: string | null,
  booked = false,
  exact = false,
): void {
  try {
    localStorage.setItem(
      PENDING_KEY,
      JSON.stringify({ nav, booked, exact } satisfies PendingSave),
    );
  } catch {
    // Private mode. The save below still runs; only resumption is lost.
  }
}

export function clearOnboardingPending(): void {
  try {
    localStorage.removeItem(PENDING_KEY);
  } catch {
    // ignore
  }
}

function readDraftRaw(): string | null {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    // Parsed once here purely to reject a corrupt draft before it reaches the
    // comparison — a string that cannot be answers is not "unsaved answers".
    return raw && JSON.parse(raw) ? raw : null;
  } catch {
    return null;
  }
}

/**
 * Answers the flow keeps for itself.
 *
 * The night, the neighbourhood, the plan and the seat are how the flow gets
 * from one screen to the next and back after a reload — they are not part of
 * anyone's profile, and the server strips them anyway. Excluded here so that
 * picking an event is not mistaken for an answer worth a request, which is
 * what put a "Saving…" screen between someone who had just paid and the door.
 */
const LOCAL_ONLY_KEYS = [
  "selectedEventId",
  "selectedNeighborhoodId",
  "selectedPlan",
  "reservedBookingId",
] as const;

/**
 * The answers the server requires before it will call a profile complete.
 *
 * The same rule `routes/user/index.ts` uses to stamp `quizCompletedAt`, via
 * `shared/quizCompletion`. Shared deliberately: the client reports the signup
 * on the write that completes the profile, and reporting a signup the server
 * didn't record is worse than not reporting one.
 */
function isProfileComplete(answers: Record<string, any>): boolean {
  return isQuizComplete(answers);
}

/**
 * Whether a save can be trusted to have landed.
 *
 * A 200 is not enough. The save route used to `UPDATE ... WHERE id` and report
 * success whether or not a row matched, and for a brand-new account the row
 * is created by the profile GET, which can finish *after* the first autosave
 * — so the write that carried every answer given before sign-in updated
 * nothing, was marked as saved here, and was never sent again. Dozens of
 * accounts ended up with no answers at all.
 *
 * The server now creates the row before writing, and says in its response
 * whether the profile is complete. This checks that verdict against what was
 * sent: a complete set of answers the server still calls incomplete did not
 * land, whatever the status code, and must be sent again. A response with no
 * verdict (an older server) is taken at its word.
 */
export function saveConfirmed(
  written: Record<string, any>,
  response: { data?: { quizCompleted?: boolean } } | null | undefined,
): boolean {
  const verdict = response?.data?.quizCompleted;
  if (verdict === undefined) return true;
  return verdict || !isProfileComplete(written);
}

/** Set once `quiz_completed` has been reported, so it is reported once. */
const SIGNUP_TRACKED_KEY = "onboardingSignupTracked";

function signupAlreadyTracked(): boolean {
  try {
    return localStorage.getItem(SIGNUP_TRACKED_KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * The signup itself: an account exists and its profile is complete.
 *
 * This is the funnel's second step (`quiz_started` → `quiz_completed` →
 * `purchase`) and the experiment's "Completed signups" metric, so *when* it
 * fires decides whether the two arms are comparable. The stepper fires it on
 * the save that follows its account gate, which is the last thing it does. The
 * v3 flow only fired it from `run()` — the end of a flow that continues
 * through the welcome, the contact screen, the night picker and the offer —
 * so a user who signed up and then browsed instead of buying never counted as
 * a signup at all, and neither did anyone leaving by an escape hatch, which
 * navigates away without finishing. Measured over the first week: 16 accounts
 * created against 4 `quiz_completed`.
 *
 * Reported here instead, on the write that completes the profile, which is the
 * same moment the server stamps `quizCompletedAt` and the same moment the
 * stepper reports. Marked on disk rather than in a ref because signing in can
 * reload the page.
 */
function trackSignupOnce(answers: Record<string, any>): void {
  if (signupAlreadyTracked()) return;
  if (!isProfileComplete(answers)) return;
  try {
    localStorage.setItem(SIGNUP_TRACKED_KEY, "1");
  } catch {
    // Private mode. Reporting it twice is better than not at all.
  }
  tracking.quizCompleted();
}

/** Clears the marker, so a fresh signup in this browser reports again. */
export function clearSignupTracked(): void {
  try {
    localStorage.removeItem(SIGNUP_TRACKED_KEY);
  } catch {
    // ignore
  }
}

/** The answers as the server will see them, serialised for comparison. */
export function savableAnswers(answers: Record<string, any> | null): string {
  const kept: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(answers ?? {})) {
    if ((LOCAL_ONLY_KEYS as readonly string[]).includes(key)) continue;
    kept[key] = value;
  }
  return JSON.stringify(kept);
}

/** The profile write both the autosave and the final save send. */
function buildProfilePayload(answers: Record<string, any>) {
  const { phone, instagram, ...rest } = answers;
  const quizAnswers = JSON.parse(savableAnswers(rest));
  // First-party ad attribution captured from the landing URL — written once
  // server-side (`onConflictDoNothing` on userId), so sending it with every
  // write is safe and means an abandoned signup is still attributable.
  const attr = getStoredFacebookAttribution();
  return {
    quizAnswers: quizAnswers as any,
    phone: phone as string,
    instagram: instagram as string,
    ...(attr
      ? {
          signupAttribution: {
            utmSource: attr.utm_source,
            utmMedium: attr.utm_medium,
            utmCampaign: attr.utm_campaign,
            utmContent: attr.utm_content,
            utmTerm: attr.utm_term,
            fbclid: attr.fbclid,
            gclid: attr.gclid ?? null,
            landingPage: attr.landingPage ?? null,
          },
        }
      : {}),
  };
}

/**
 * How long after a screen change to write.
 *
 * Not a debounce — the trigger is already one per screen. It is only there to
 * keep the request off the frame the new screen is mounting on, since starting
 * a fetch is not free and that frame is the one the user is watching.
 */
const WRITE_DELAY_MS = 400;

/** How long to wait before re-checking a write that overlapped another. */
const IN_FLIGHT_RETRY_MS = 500;

/**
 * The answers as they were when the server last acknowledged them.
 *
 * Kept on disk rather than in a ref because the two halves of saving live in
 * different hooks — and, more to the point, can live in different page loads:
 * a sign-in that navigates away and comes back leaves any in-memory record
 * behind.
 */
const SAVED_KEY = "onboardingSavedAnswers";

function readSaved(): string | null {
  try {
    return localStorage.getItem(SAVED_KEY);
  } catch {
    return null;
  }
}

function markSaved(serialised: string): void {
  try {
    localStorage.setItem(SAVED_KEY, serialised);
  } catch {
    // Private mode. Worst case, finishing saves something already saved.
  }
}

function clearSaved(): void {
  try {
    localStorage.removeItem(SAVED_KEY);
  } catch {
    // ignore
  }
}

/**
 * Whether finishing still owes the server anything.
 *
 * Once signed in, answers go through as they are given — so by the last screen
 * the server usually has all of them already, and re-sending them buys nothing
 * but a full-screen "Saving…" between the user and the door. This is what lets
 * that screen appear only when there is genuinely something to write: an
 * answer changed inside the debounce window, an autosave that failed, or a
 * flow finished without signing in until the end.
 */
export function hasUnsavedAnswers(
  draft: string | null,
  saved: string | null,
): boolean {
  if (!draft || draft === "{}") return false;
  return draft !== saved;
}

/**
 * Writes answers through as they are given, once there is an account to write
 * them to.
 *
 * Onboarding used to persist nothing until the very last screen, so everything
 * between signing in and finishing existed only in this browser's
 * `localStorage`. Close the tab at the offer and the profile the user had just
 * filled in was gone — and because the flow signs people in *mid-way*, that
 * window covers a third of it.
 *
 * Safe to run repeatedly: the server merges `quizAnswers` into what it already
 * has rather than replacing them, so a partial write can only ever add. It
 * also means `quizCompletedAt` is stamped as soon as the required fields are
 * present, instead of waiting for a finish the user may never reach.
 *
 * Deliberately quiet: no navigation, no draft clearing, no `quiz_completed`.
 * Finishing owns all three.
 *
 * Triggered by the *step*, not by the answers. Writing on every answer meant a
 * request landed in the middle of a question — several of them on a
 * multi-select — each one arriving back while the user was still tapping. A
 * screen change is the natural seam: nothing is half-answered, and whatever
 * they did on the screen they just left is what gets sent. Answers given and
 * then abandoned without moving on are still on disk in the draft, which is
 * what the resume path reads.
 *
 * @param step the current screen's id. Any change to it triggers a write.
 */
export function useOnboardingAutosave(
  answers: Record<string, any>,
  step: string | null,
): void {
  const { isSignedIn } = useAuth();
  // Silent: see `useUpdateProfile`. A background write of what the user is
  // still typing must not pull the profile back down and re-render the flow
  // around them.
  const updateProfile = useUpdateProfile({ silent: true });
  // The one exception: the write that completes the profile. Finishing
  // decides whether it still owes a write by what the profile says, so the
  // profile has to learn about this one — once, not on every step.
  const invalidateProfile = useInvalidateProfile();
  // In a ref, like `save`: a new function each render must not re-arm the
  // timer.
  const invalidate = useRef(invalidateProfile);
  invalidate.current = invalidateProfile;
  const completionSeen = useRef(false);
  // Held in a ref so a new mutation object each render doesn't re-arm the timer.
  const save = useRef(updateProfile.mutateAsync);
  save.current = updateProfile.mutateAsync;
  const lastWritten = useRef<string | null>(null);
  const inFlight = useRef(false);
  // Read at write time rather than depended on: the trigger is the step, and
  // the answers are simply whatever they are when it fires.
  const answersRef = useRef(answers);
  answersRef.current = answers;
  // Arriving somewhere is not the same as leaving somewhere. The first run is
  // the flow mounting — on a resume that would fire a request for answers the
  // server already has, before the user has done anything.
  const seenAStep = useRef(false);

  useEffect(() => {
    const first = !seenAStep.current;
    seenAStep.current = true;
    if (!isSignedIn || first) return;
    // Finishing is already writing this, and it clears the draft afterwards.
    if (readPending()) return;
    // The projection the server actually receives, so a change it would never
    // see — the night they tapped, the plan they pressed — does not spend a
    // request.
    const serialised = savableAnswers(answersRef.current);
    if (serialised === "{}" || serialised === lastWritten.current) return;

    let cancelled = false;
    let id: ReturnType<typeof setTimeout>;

    const attempt = () => {
      if (cancelled) return;
      // A previous write is still out. Waiting is right — two writes of
      // overlapping answers race — but returning outright dropped this change
      // until the user happened to answer something else, which on the last
      // question of the flow is never.
      if (inFlight.current) {
        id = setTimeout(attempt, IN_FLIGHT_RETRY_MS);
        return;
      }
      inFlight.current = true;
      void (async () => {
        try {
          const written = JSON.parse(serialised) as Record<string, any>;
          const response = await save.current(buildProfilePayload(written));
          if (!saveConfirmed(written, response)) {
            // Left unmarked, like a failure: the next step re-sends, and
            // finishing re-sends regardless. See `saveConfirmed`.
            console.error("[onboarding] Autosave not confirmed by server");
            return;
          }
          lastWritten.current = serialised;
          markSaved(serialised);
          // The write landed and the profile is complete: that is the signup.
          // See `trackSignupOnce`.
          trackSignupOnce(written);
          if (response?.data?.quizCompleted && !completionSeen.current) {
            completionSeen.current = true;
            void invalidate.current();
          }
        } catch (e) {
          // Left unmarked so the next change retries. The draft is still on
          // disk, and finishing re-sends everything regardless.
          console.error("[onboarding] Autosave failed", e);
        } finally {
          inFlight.current = false;
        }
      })();
    };

    id = setTimeout(attempt, WRITE_DELAY_MS);

    return () => {
      cancelled = true;
      clearTimeout(id);
    };
  }, [step, isSignedIn]);
}

/**
 * Whether finishing still owes the server a write.
 *
 * Two reasons it might. The draft differs from what the server last
 * acknowledged — an answer changed inside the debounce window, an autosave
 * that failed, a flow finished by someone who only signed in at the end. Or
 * the server has never called this profile complete while the draft is: the
 * autosave that carried the answers may have been acknowledged without
 * landing (see `saveConfirmed`), and a profile the server does not consider
 * finished is one the app will bounce the user back into onboarding for. In
 * that state, not writing is the one thing that cannot be right.
 *
 * On the ordinary path neither is true: the autosave that completed the
 * profile refreshed it, so `serverComplete` is known and finishing is a
 * navigation, not a request.
 */
export function finishOwesWrite(
  draft: string | null,
  saved: string | null,
  serverComplete: boolean,
): boolean {
  if (!draft || draft === "{}") return false;
  if (hasUnsavedAnswers(draft, saved)) return true;
  return !serverComplete && isProfileComplete(JSON.parse(draft));
}

export function useOnboardingSave() {
  const { isSignedIn } = useAuth();
  const { navigate } = useAppNavigation();
  const updateProfile = useUpdateProfile();
  const invalidateProfile = useInvalidateProfile();
  const guidedBooking = useGuidedPostSignupBooking();
  // What the server currently says about this profile. Read through a ref so
  // `run` does not change identity — and re-run the resume effect — every
  // time the profile query settles.
  const { data: profileData } = useProfile();
  const serverComplete = useRef(false);
  serverComplete.current = Boolean(profileData?.data?.user?.quizCompletedAt);
  // A save in flight must not be started twice — by the finish handler and the
  // resume effect together, or by a re-render of either.
  const running = useRef(false);

  const run = useCallback(
    /**
     * @param leaveRegardless the user pressed finish (and may just have paid),
     *   so they are owed the destination whatever the save does. A resume, by
     *   contrast, happens on some page load the user chose — it must not yank
     *   them somewhere else because a marker was lying around.
     * @param exact `nav` is the path itself, not a hint to be resolved.
     */
    async (
      nav: string | null,
      leaveRegardless = false,
      booked = false,
      exact = false,
    ) => {
      if (running.current) return;
      const destination =
        exact && nav
          ? nav
          : resolvePostQuizDestination(
              nav,
              // Never for someone who already holds a seat: /booking/start
              // exists to get a first booking out of ad traffic, and putting
              // it in front of someone who has just made one is a screen with
              // nothing to do.
              guidedBooking && !booked,
              getStoredAdEventType(),
            );
      const draft = readDraftRaw();
      const savable = savableAnswers(draft ? JSON.parse(draft) : null);
      if (!draft) {
        // Nothing to save: clear the marker so it can't strand the user in a
        // resume loop on every page load.
        clearOnboardingPending();
        if (leaveRegardless) navigate(destination);
        return;
      }
      running.current = true;

      try {
        if (finishOwesWrite(savable, readSaved(), serverComplete.current)) {
          const written = JSON.parse(draft) as Record<string, any>;
          const response = await updateProfile.mutateAsync(
            buildProfilePayload(written),
          );
          // Thrown rather than ignored so this takes the failure path below:
          // the draft and the marker stay, and the next load sends it again.
          if (!saveConfirmed(written, response)) {
            throw new Error("Profile save not confirmed by server");
          }
          markSaved(savable);
        }

        localStorage.removeItem(DRAFT_KEY);
        clearSaved();
        clearOnboardingPending();
        // Normally already reported, by the autosave that completed the
        // profile. This covers the paths that never autosaved: a flow
        // finished by someone who only signed in at the very end, or a write
        // that failed every time until this one.
        trackSignupOnce(JSON.parse(draft) as Record<string, any>);
        await invalidateProfile();
        navigate(destination);
      } catch (e) {
        // The marker stays: the answers are still owed, and the next load —
        // or the user's next attempt — picks it up rather than losing them.
        console.error("[onboarding] Save failed, will retry on next load", e);
        // Leaving anyway is the lesser evil on the finish path. The offer is
        // the last screen, and someone who has just been charged sitting on it
        // watching nothing happen has no way to tell a failed save from a
        // failed payment.
        if (leaveRegardless) navigate(destination);
      } finally {
        running.current = false;
      }
    },
    [updateProfile, invalidateProfile, navigate, guidedBooking],
  );

  /**
   * Finish the flow. Marks first so an interruption between here and the
   * server is recoverable.
   */
  const finish = useCallback(
    (nav: string | null, booked = false) => {
      markOnboardingPending(nav, booked);
      void run(nav, true, booked);
    },
    [run],
  );

  /**
   * Leave the flow early for exactly `path`. The same save as `finish`, so
   * an escape hatch cannot strand answers in the browser — see
   * `OnboardingRuntime.leave` for the loop that did.
   */
  const leave = useCallback(
    (path: string, booked = false) => {
      markOnboardingPending(path, booked, true);
      void run(path, true, booked, true);
    },
    [run],
  );

  // Resume: a save was owed and the page has come back signed in. Covers the
  // redirect-SSO round trip, a reload mid-save, and a failed request.
  useEffect(() => {
    if (!isSignedIn) return;
    const pending = readPending();
    if (!pending) return;
    void run(pending.nav, false, pending.booked, pending.exact);
  }, [isSignedIn, run]);

  return { finish, leave, isSaving: updateProfile.isPending };
}
